/**
 * useCustomers Hook
 * Provides access to customer data with CRUD operations
 */

import { useAtom } from "jotai";
import { useMemo, useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { customersQueryAtom } from "@/store/referenceDataStore";
import { servicesAPI } from "@/services";
import type { IServiceCustomer } from "@/services";
import { invalidateWithTimeout } from "@/utils/queryUtils";

export interface UseCustomersReturn {
  /** All customers from API */
  customers: IServiceCustomer[];
  /** Active (non-archived) customers */
  activeCustomers: IServiceCustomer[];
  /** Customers sorted alphabetically by name */
  sortedCustomers: IServiceCustomer[];
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
  /** Refetch function */
  refetch: () => void;
  /** Find customer by ID */
  findById: (id: number) => IServiceCustomer | undefined;
  /** Find customer by name */
  findByName: (name: string) => IServiceCustomer | undefined;
  /** Create a new customer */
  createCustomer: (name: string) => Promise<IServiceCustomer[] | null>;
  /** Delete a customer */
  deleteCustomer: (id: number) => Promise<boolean>;
  /** Creating state */
  isCreating: boolean;
  /** Create error */
  createError: Error | null;
  /** Deleting state */
  isDeleting: boolean;
  /** Delete error */
  deleteError: Error | null;
}

export function useCustomers(): UseCustomersReturn {
  const [query] = useAtom(customersQueryAtom);
  const queryClient = useQueryClient();
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<Error | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<Error | null>(null);

  // Stable reference to prevent unnecessary recalculations
  const customers = useMemo(() => query.data ?? [], [query.data]);

  // Active customers (backend returns only active by default)
  const activeCustomers = customers;

  // Sort alphabetically by name
  const sortedCustomers = useMemo(
    () => [...customers].sort((a, b) => a.name.localeCompare(b.name)),
    [customers],
  );

  // Find by ID
  const findById = useCallback(
    (id: number) => customers.find((c) => c.id === id),
    [customers],
  );

  // Find by name
  const findByName = useCallback(
    (name: string) =>
      customers.find((c) => c.name.toLowerCase() === name.toLowerCase()),
    [customers],
  );

  // Create customer mutation
  const createCustomer = useCallback(
    async (name: string): Promise<IServiceCustomer[] | null> => {
      setIsCreating(true);
      setCreateError(null);

      try {
        const result = await servicesAPI.createCustomer(name);
        // Invalidate and refetch customers
        await invalidateWithTimeout(queryClient, ["customers"]);
        return result;
      } catch (error) {
        setCreateError(error as Error);
        return null;
      } finally {
        setIsCreating(false);
      }
    },
    [queryClient],
  );

  const deleteCustomer = useCallback(
    async (id: number): Promise<boolean> => {
      setIsDeleting(true);
      setDeleteError(null);

      try {
        await servicesAPI.deleteCustomer(id);
        await invalidateWithTimeout(queryClient, ["customers"]);
        return true;
      } catch (error) {
        setDeleteError(error as Error);
        return false;
      } finally {
        setIsDeleting(false);
      }
    },
    [queryClient],
  );

  return {
    customers,
    activeCustomers,
    sortedCustomers,
    isLoading: query.isLoading,
    error: query.error ?? null,
    refetch: query.refetch,
    findById,
    findByName,
    createCustomer,
    deleteCustomer,
    isCreating,
    createError,
    isDeleting,
    deleteError,
  };
}
